// Users modals module for Docospot
// Handles create/edit user modals and CRUD operations

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall, getCurrentUserFromAPI } from '../core/api.js';
import { showError, showSuccess, showConfirmDialog, showWarning } from '../core/ui.js';
import { getCurrentUser, getEffectiveAccountId } from '../utils/helpers.js';
import { loadUsersData } from './users.js';
import { initAccountSearch } from '../utils/account-search.js';

/**
 * Parse a comma-separated NFC card input into a clean, deduplicated array
 * of lowercased ids. Tolerates semicolons, newlines and extra whitespace.
 */
function parseNfcCardInput(raw) {
    if (!raw) return [];
    const seen = new Set();
    const result = [];
    for (const part of String(raw).replace(/;|\n/g, ',').split(',')) {
        const norm = part.trim().toLowerCase();
        if (!norm || seen.has(norm)) continue;
        seen.add(norm);
        result.push(norm);
    }
    return result;
}

/**
 * Show or hide an inline error banner inside a modal.
 * Falls back to the global toast if the banner element is missing.
 */
function showModalError(bannerId, message) {
    const banner = document.getElementById(bannerId);
    if (banner) {
        banner.textContent = message;
        banner.classList.remove('hidden');
        // Scroll the banner into view at the top of the modal
        banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
        showError(message);
    }
}

function clearModalError(bannerId) {
    const banner = document.getElementById(bannerId);
    if (banner) {
        banner.textContent = '';
        banner.classList.add('hidden');
    }
}

/**
 * Show create user modal
 */
export async function showCreateUserModal() {
    const modal = document.getElementById('modal');
    if (!modal) return;

    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">${t('users.createTitle')}</h3>
                    <div id="create-user-error" class="hidden mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"></div>
                    <form id="create-user-form" class="space-y-4">
                        <!-- Role - Full Width -->
                        <div>
                            <label for="user-role" class="block text-sm font-medium text-gray-700">${t('users.role')}</label>
                            <select id="user-role" name="role" onchange="toggleWelcomeEmailOption()"
                                    class="mt-1 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="Administrator">Administrator</option>
                                <option value="Team leader">Team leader</option>
                                <option value="Team viewer">Team viewer</option>
                                <option value="Bookkeeper">Bookkeeper</option>
                                <option value="Team member" selected>Team member</option>
                                <option value="External contact">External contact</option>
                            </select>
                        </div>
                        
                        <!-- Account Selection - Full Width -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('locations.selectAccount')} <span class="text-red-500">*</span></label>
                            <!-- Search Field -->
                            <div class="relative mb-2">
                                <input type="text" 
                                       id="user-account-search" 
                                       placeholder="${t('users.searchAccountsPlaceholder')}" 
                                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <!-- Search Results Dropdown -->
                                <div id="user-account-search-results" 
                                     class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                </div>
                            </div>
                            <!-- Account Tree -->
                            <div id="account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('users.loadingAccounts')}</div>
                            </div>
                            <input type="hidden" id="user-account" name="account_id" required>
                        </div>
                        
                        <!-- Two Column Layout for Input Fields -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label for="user-employee-id" class="block text-sm font-medium text-gray-700">${t('users.employeeId')}</label>
                                <input type="text" id="user-employee-id" name="employee_id" 
                                       class="mt-1 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="f.eks., EMP-12345">
                            </div>
                            <div>
                                <label for="user-full-name" class="block text-sm font-medium text-gray-700">${t('users.fullName')}</label>
                                <input type="text" id="user-full-name" name="full_name" 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div>
                                <label for="user-nfc-card-id" class="block text-sm font-medium text-gray-700">${t('users.nfcCardId')}</label>
                                <input type="text" id="user-nfc-card-id" name="nfc_card_id" 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="Typisk 10 tegn"
                                       maxlength="500">
                                <p class="mt-1 text-xs text-gray-500">${t('users.nfcCardIdHint')}</p>
                            </div>
                            <div id="email-field-container">
                                <label for="user-email" class="block text-sm font-medium text-gray-700">${t('users.email')} <span id="email-optional-label"></span></label>
                                <input type="email" id="user-email" name="email" required 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div>
                                <label for="user-phone" class="block text-sm font-medium text-gray-700">${t('users.phoneNumber')}</label>
                                <input type="tel" id="user-phone" name="phone_number" 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="f.eks., +45 12 34 56 78">
                            </div>
                            <div>
                                <label for="user-department" class="block text-sm font-medium text-gray-700">${t('users.department')}</label>
                                <input type="text" id="user-department" name="department" 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="f.eks., IT, HR, Salg">
                            </div>
                            <div>
                                <label for="user-work-address" class="block text-sm font-medium text-gray-700">${t('users.workAddress')}</label>
                                <input type="text" id="user-work-address" name="work_address" 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="f.eks., Haderslev, Danmark">
                            </div>
                        </div>
                        
                        <!-- Checkboxes - Full Width -->
                        <div id="welcome-email-container">
                            <div class="flex items-center">
                                <input type="checkbox" id="send-welcome-email" name="send_welcome_email" checked
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <label for="send-welcome-email" class="ml-2 block text-sm text-gray-900">
                                    ${t('users.sendWelcomeEmail')}
                                </label>
                            </div>
                            <div class="text-xs text-gray-500 ml-6">
                                ${t('users.sendWelcomeEmailDesc')}
                            </div>
                        </div>
                        <div id="grant-access-container" class="border-t border-gray-200 pt-4">
                            <div class="flex items-center">
                                <input type="checkbox" id="user-is-active" name="is_active" checked
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <label for="user-is-active" class="ml-2 block text-sm text-gray-900">
                                    ${t('users.grantAccess')}
                                </label>
                            </div>
                            <div class="text-xs text-gray-500 ml-6">
                                ${t('users.noSystemAccess')}
                            </div>
                        </div>
                    </form>
                    <div class="flex items-center justify-end space-x-3 mt-6">
                        <button onclick="closeModal()" 
                                class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                            ${t('common.cancel')}
                        </button>
                        <button onclick="createUser()" 
                                class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                            ${t('users.createUserButton')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    modal.classList.remove('hidden');
    
    // Apply initial visibility state based on default role (Team member)
    toggleWelcomeEmailOption();
    
    // Load accounts for dropdown
    loadAccountsForUserModal();
}

/**
 * Toggle email requirement based on role (Team members don't need email)
 */
export function toggleWelcomeEmailOption() {
    const roleSelect = document.getElementById('user-role');
    const welcomeEmailContainer = document.getElementById('welcome-email-container');
    const grantAccessContainer = document.getElementById('grant-access-container');
    const emailInput = document.getElementById('user-email');
    const emailOptionalLabel = document.getElementById('email-optional-label');
    
    if (!roleSelect || !welcomeEmailContainer || !emailInput) return;
    
    const selectedRole = roleSelect.value;
    
    // Team members don't need system access, so hide welcome email and grant access options
    if (selectedRole === 'Team member') {
        welcomeEmailContainer.style.display = 'none';
        if (grantAccessContainer) {
            grantAccessContainer.style.display = 'none';
        }
        emailInput.removeAttribute('required');
        if (emailOptionalLabel) {
            emailOptionalLabel.textContent = `(${t('common.optional')})`;
            emailOptionalLabel.className = 'text-gray-500 text-sm';
        }
    } else {
        welcomeEmailContainer.style.display = 'block';
        if (grantAccessContainer) {
            grantAccessContainer.style.display = 'block';
        }
        emailInput.setAttribute('required', 'required');
        if (emailOptionalLabel) {
            emailOptionalLabel.textContent = '';
        }
    }
}

/**
 * Load accounts tree for create modal
 */
async function loadAccountsForUserModal() {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const container = document.getElementById('account-tree-container');
        if (!container) return;
        
        if (response && response.accounts_tree) {
            container.innerHTML = renderAccountTree(response.accounts_tree);
            
            // Initialize account search
            initAccountSearch(
                'user-account-search',
                'user-account-search-results',
                'account-tree-container',
                'user-account'
            );

            // Pre-select the current administrator's account by default
            const defaultAccountId = getEffectiveAccountId();
            if (defaultAccountId) {
                const radio = container.querySelector(
                    `input[name="account-selection"][value="${defaultAccountId}"]`
                );
                if (radio) {
                    radio.checked = true;
                    selectAccount(defaultAccountId);

                    // Expand any collapsed ancestor tree containers so the
                    // selected account is visible to the administrator
                    let parent = radio.closest('[id^="tree-"]');
                    while (parent && parent.id.startsWith('tree-')) {
                        parent.classList.remove('hidden');
                        const iconId = parent.id.replace('tree-', 'tree-icon-');
                        const icon = document.getElementById(iconId);
                        if (icon) icon.classList.add('rotate-90');
                        parent = parent.parentElement
                            ? parent.parentElement.closest('[id^="tree-"]')
                            : null;
                    }
                }
            }
        } else {
            container.innerHTML = `<div class="text-gray-500 text-sm">${t('users.noAccountsFound')}</div>`;
        }
            
    } catch (error) {
        console.error('Error loading accounts:', error);
        const container = document.getElementById('account-tree-container');
        if (container) {
            container.innerHTML = `<div class="text-red-500 text-sm">${t('users.errorLoadingAccounts')}</div>`;
        }
    }
}

/**
 * Render account tree hierarchy for create modal
 */
function renderAccountTree(accounts, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleAccountTreeItem('tree-${accountId}')" 
                                class="mr-1 w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600">
                            <svg id="tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="account-selection" value="${accountId}" 
                               onchange="selectAccount('${accountId}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="tree-${accountId}" class="hidden">
                        ${renderAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Toggle account tree item expand/collapse
 */
export function toggleAccountTreeItem(itemId) {
    const item = document.getElementById(itemId);
    
    // Determine the correct icon ID based on the item ID prefix
    let iconId;
    if (itemId.startsWith('edit-tree-')) {
        iconId = itemId.replace('edit-tree-', 'edit-tree-icon-');
    } else {
        iconId = itemId.replace('tree-', 'tree-icon-');
    }
    
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

/**
 * Select account in create modal
 */
export function selectAccount(accountId) {
    const hiddenInput = document.getElementById('user-account');
    if (hiddenInput) {
        hiddenInput.value = accountId;
    }
}

/**
 * Create a new user
 */
export async function createUser() {
    try {
        // Clear any previous error states
        clearModalError('create-user-error');
        const emailField = document.getElementById('user-email');
        if (emailField) {
            emailField.classList.remove('border-red-500');
        }
        const nfcFieldReset = document.getElementById('user-nfc-card-id');
        if (nfcFieldReset) {
            nfcFieldReset.classList.remove('border-red-500');
        }
        
        const form = document.getElementById('create-user-form');
        const formData = new FormData(form);
        
        const role = formData.get('role');
        const isTeamMember = role === 'Team member';
        
        const userData = {
            role: role,
            account_id: formData.get('account_id'),
            full_name: formData.get('full_name'),
            employee_id: formData.get('employee_id') || '',
            phone_number: formData.get('phone_number') || '',
            department: formData.get('department') || '',
            nfc_card_ids: parseNfcCardInput(formData.get('nfc_card_id')),
            work_address: formData.get('work_address') || '',
            is_active: document.getElementById('user-is-active').checked,
            send_welcome_email: isTeamMember ? false : document.getElementById('send-welcome-email').checked
        };
        
        // Only include email if it's provided (Team Members don't need email)
        const email = formData.get('email');
        if (email && email.trim()) {
            userData.email = email.trim();
        }
        
        if (!userData.account_id) {
            showError(t('users.selectAccountRequired'));
            return;
        }
        
        await apiCall('/users/', {
            method: 'POST',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify(userData)
        });
        
        closeModal();
        showSuccess(t('users.createSuccess'));
        loadUsersData(true);  // Reset and reload from beginning to show new user
    } catch (error) {
        // Check if it's an email duplicate error
        if (error.message && error.message.toLowerCase().includes('email already')) {
            const emailField = document.getElementById('user-email');
            if (emailField) {
                emailField.classList.add('border-red-500');
            }
            showModalError('create-user-error', t('users.emailAlreadyRegistered'));
        } else if (error.message && error.message.toLowerCase().includes('nfc card id')) {
            // NFC card already assigned to another user
            const nfcField = document.getElementById('user-nfc-card-id');
            if (nfcField) {
                nfcField.classList.add('border-red-500');
                nfcField.focus();
            }
            showModalError('create-user-error', error.message);
        } else {
            showModalError('create-user-error', t('users.createError') + ': ' + (error.message || 'Ukendt fejl'));
        }
        console.error('Error creating user:', error);
    }
}

/**
 * Edit user - loads user data and shows edit modal
 */
export async function editUser(userId) {
    try {
        console.log('🔧 Edit User - Received userId:', userId);
        
        if (!userId || userId === 'undefined' || userId === 'null') {
            throw new Error('Invalid user ID: ' + userId);
        }
        
        // Fetch user data
        const user = await apiCall(`/users/${userId}`, {
            headers: auth.getAuthHeaders()
        });
        
        console.log('✅ User data loaded:', user);
        showEditUserModal(user);
    } catch (error) {
        console.error('❌ Edit User Error:', error);
        showError('Kunne ikke indlæse brugerdata: ' + (error.message || 'Ukendt fejl'));
    }
}

/**
 * Show edit user modal
 */
async function showEditUserModal(user) {
    const modal = document.getElementById('modal');
    if (!modal) return;
    
    // Check if current user can delete users (SuperAdmin or Administrator)
    const currentUser = getCurrentUser();
    const isSuperAdmin = currentUser && currentUser.role === 'SuperAdmin';
    const canDeleteUsers = currentUser && (currentUser.role === 'SuperAdmin' || currentUser.role === 'Administrator');

    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">${t('users.edit')}</h3>
                    <div id="edit-user-error" class="hidden mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"></div>
                    <form id="edit-user-form" class="space-y-4">
                        <input type="hidden" id="edit-user-id" value="${user.id}">
                        
                        <!-- Role - Full Width -->
                        <div>
                            <label for="edit-user-role" class="block text-sm font-medium text-gray-700">${t('users.role')}</label>
                            <select id="edit-user-role" name="role" onchange="toggleEditWelcomeEmailOption()"
                                    class="mt-1 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="Administrator" ${user.role === 'Administrator' ? 'selected' : ''}>Administrator</option>
                                <option value="Team leader" ${user.role === 'Team leader' ? 'selected' : ''}>Team leader</option>
                                <option value="Team viewer" ${user.role === 'Team viewer' ? 'selected' : ''}>Team viewer</option>
                                <option value="Bookkeeper" ${user.role === 'Bookkeeper' ? 'selected' : ''}>Bookkeeper</option>
                                <option value="Team member" ${user.role === 'Team member' ? 'selected' : ''}>Team member</option>
                                <option value="External contact" ${user.role === 'External contact' ? 'selected' : ''}>External contact</option>
                            </select>
                        </div>
                        
                        <!-- Account Selection - Full Width -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('locations.selectAccount')} <span class="text-red-500">*</span></label>
                            <!-- Search Field -->
                            <div class="relative mb-2">
                                <input type="text" 
                                       id="edit-user-account-search" 
                                       placeholder="${t('users.searchAccountsPlaceholder')}" 
                                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <!-- Search Results Dropdown -->
                                <div id="edit-user-account-search-results" 
                                     class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                </div>
                            </div>
                            <!-- Account Tree -->
                            <div id="edit-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('users.loadingAccounts')}</div>
                            </div>
                            <input type="hidden" id="edit-user-account" name="account_id" required>
                        </div>

                        <!-- Time Tracking Account Access (Team leader, Team viewer & Bookkeeper only) -->
                        <div id="edit-tt-accounts-section" class="${['Team leader', 'Team viewer', 'Bookkeeper'].includes(user.role) ? '' : 'hidden'} border border-blue-200 rounded-md p-4 bg-blue-50">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Tidsregistrering - ekstra kontoadgang</label>
                            <p class="text-xs text-gray-500 mb-3">Vælg hvilke kontoer denne bruger kan se tidsregistreringer for (udover eget konto-hierarki)</p>
                            <div id="edit-tt-accounts-tree" class="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">Indlæser kontoer...</div>
                            </div>
                        </div>

                        <!-- Two Column Layout for Input Fields -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label for="edit-user-employee-id" class="block text-sm font-medium text-gray-700">${t('users.employeeId')}</label>
                                <input type="text" id="edit-user-employee-id" name="employee_id" value="${user.employee_id || ''}"
                                       class="mt-1 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="f.eks., EMP-12345">
                            </div>
                            <div>
                                <label for="edit-user-full-name" class="block text-sm font-medium text-gray-700">${t('users.fullName')}</label>
                                <input type="text" id="edit-user-full-name" name="full_name" value="${user.full_name || ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div>
                                <label for="edit-user-nfc-card-id" class="block text-sm font-medium text-gray-700">${t('users.nfcCardId')}</label>
                                <input type="text" id="edit-user-nfc-card-id" name="nfc_card_id" value="${(user.nfc_card_ids && user.nfc_card_ids.length ? user.nfc_card_ids.join(', ') : (user.nfc_card_id || ''))}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="Typically 10 characters"
                                       maxlength="500">
                                <p class="mt-1 text-xs text-gray-500">${t('users.nfcCardIdHint')}</p>
                            </div>
                            <div>
                                <label for="edit-user-email" class="block text-sm font-medium text-gray-700">${t('users.email')}</label>
                                <input type="email" id="edit-user-email" name="email" value="${user.email || ''}" required 
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div>
                                <label for="edit-user-phone" class="block text-sm font-medium text-gray-700">${t('users.phoneNumber')}</label>
                                <input type="tel" id="edit-user-phone" name="phone_number" value="${user.phone_number || ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div>
                                <label for="edit-user-department" class="block text-sm font-medium text-gray-700">${t('users.department')}</label>
                                <input type="text" id="edit-user-department" name="department" value="${user.department || ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                            </div>
                            <div>
                                <label for="edit-user-work-address" class="block text-sm font-medium text-gray-700">${t('users.workAddress')}</label>
                                <input type="text" id="edit-user-work-address" name="work_address" value="${user.work_address || ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="e.g., Haderslev, Denmark">
                            </div>
                        </div>
                        
                        <!-- Checkboxes - Full Width -->
                        ${!user.password_set && user.role !== 'Team member' ? `
                        <div id="edit-welcome-email-container" class="flex items-center">
                            <input type="checkbox" id="edit-send-welcome-email" name="send_welcome_email"
                                   class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                            <label for="edit-send-welcome-email" class="ml-2 block text-sm text-gray-900">
                                ${t('users.resendWelcomeEmail')}
                            </label>
                        </div>
                        ` : ''}
                        ${user.role !== 'Team member' ? `
                        <div id="edit-grant-access-container" class="border-t border-gray-200 pt-4">
                            <div class="flex items-center">
                                <input type="checkbox" id="edit-user-is-active" name="is_active" ${user.is_active !== false ? 'checked' : ''}
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <label for="edit-user-is-active" class="ml-2 block text-sm text-gray-900">
                                    ${t('users.grantAccess')}
                                </label>
                            </div>
                            <div class="text-xs text-gray-500 ml-6">
                                ${t('users.noSystemAccess')}
                            </div>
                        </div>
                        ` : ''}
                    </form>
                    <div class="flex items-center justify-between mt-6">
                        ${canDeleteUsers ? `
                        <button onclick="deleteUser('${user.id}', '${(user.full_name || user.username).replace(/'/g, "\\'")}')"
                                class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                            ${t('users.delete')}
                        </button>
                        ` : '<div></div>'}
                        <div class="flex items-center space-x-3">
                            <button onclick="closeModal()" 
                                    class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                ${t('common.cancel')}
                            </button>
                            <button onclick="updateUser()" 
                                    class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                                ${t('users.updateUserButton')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    modal.classList.remove('hidden');
    
    // Load accounts and set current value
    loadAccountsForEditModal(user.account_id);

    // Load time tracking account tree if role requires it
    const ttRoles = ['Team leader', 'Team viewer', 'Bookkeeper'];
    if (ttRoles.includes(user.role)) {
        loadTimeTrackingAccountsForEdit(user.time_tracking_account_ids || []);
    }
}

/**
 * Toggle email requirement in edit modal based on role
 */
export function toggleEditWelcomeEmailOption() {
    const roleSelect = document.getElementById('edit-user-role');
    const welcomeEmailContainer = document.getElementById('edit-welcome-email-container');
    const grantAccessContainer = document.getElementById('edit-grant-access-container');
    const ttAccountsSection = document.getElementById('edit-tt-accounts-section');
    
    if (!roleSelect) return;
    
    const selectedRole = roleSelect.value;
    const showTTAccounts = selectedRole === 'Team leader' || selectedRole === 'Team viewer' || selectedRole === 'Bookkeeper';

    // Show/hide time tracking accounts section
    if (ttAccountsSection) {
        ttAccountsSection.classList.toggle('hidden', !showTTAccounts);
        // Load accounts tree if just becoming visible and not yet loaded
        if (showTTAccounts && ttAccountsSection.dataset.loaded !== 'true') {
            loadTimeTrackingAccountsForEdit([]);
        }
    }
    
    // Team members don't need system access, so hide welcome email and grant access options
    if (selectedRole === 'Team member') {
        if (welcomeEmailContainer) {
            welcomeEmailContainer.style.display = 'none';
        }
        if (grantAccessContainer) {
            grantAccessContainer.style.display = 'none';
        }
    } else {
        if (welcomeEmailContainer) {
            welcomeEmailContainer.style.display = 'flex';
        }
        if (grantAccessContainer) {
            grantAccessContainer.style.display = 'block';
        }
    }
}

/**
 * Load the account tree for the time tracking access section in the edit modal.
 * @param {string[]} selectedIds - List of account IDs that should be pre-checked.
 */
async function loadTimeTrackingAccountsForEdit(selectedIds = []) {
    const treeContainer = document.getElementById('edit-tt-accounts-tree');
    const section = document.getElementById('edit-tt-accounts-section');
    if (!treeContainer) return;

    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });

        if (response && response.accounts_tree) {
            treeContainer.innerHTML = renderTTAccountCheckboxTree(response.accounts_tree, selectedIds);
            if (section) section.dataset.loaded = 'true';
            // Auto-expand tree to show the first selected account
            if (selectedIds && selectedIds.length > 0) {
                expandTreeToAccount(response.accounts_tree, selectedIds[0], 'tt-tree-');
            }
        } else {
            treeContainer.innerHTML = '<div class="text-gray-500 text-sm">Ingen kontoer fundet</div>';
        }
    } catch (error) {
        console.error('Error loading TT accounts:', error);
        treeContainer.innerHTML = '<div class="text-red-500 text-sm">Kunne ikke indlæse kontoer</div>';
    }
}

/**
 * Render a checkbox tree for time tracking account selection.
 */
function renderTTAccountCheckboxTree(accounts, selectedIds, level = 0) {
    let html = '';
    const selectedSet = new Set(selectedIds);
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        const isChecked = selectedSet.has(accountId);

        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleAccountTreeItem('tt-tree-${accountId}')" 
                                class="mr-1 w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600">
                            <svg id="tt-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="checkbox" name="tt_account_ids" value="${accountId}"
                               ${isChecked ? 'checked' : ''}
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="tt-tree-${accountId}" class="hidden">
                        ${renderTTAccountCheckboxTree(account.children, selectedIds, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    return html;
}

/**
 * Load accounts tree for edit modal
 */
async function loadAccountsForEditModal(currentAccountId) {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const container = document.getElementById('edit-account-tree-container');
        if (!container) return;
        
        if (response && response.accounts_tree) {
            container.innerHTML = renderEditAccountTree(response.accounts_tree, currentAccountId);
            // Set the hidden input value
            const hiddenInput = document.getElementById('edit-user-account');
            if (hiddenInput) {
                hiddenInput.value = currentAccountId;
            }
            // Expand tree to show the current account
            expandTreeToAccount(response.accounts_tree, currentAccountId, 'edit-tree-');
            
            // Initialize account search
            initAccountSearch(
                'edit-user-account-search',
                'edit-user-account-search-results',
                'edit-account-tree-container',
                'edit-user-account'
            );
        } else {
            container.innerHTML = `<div class="text-gray-500 text-sm">${t('users.noAccountsFound')}</div>`;
        }
            
    } catch (error) {
        console.error('Error loading accounts:', error);
    }
}

/**
 * Render account tree for edit modal with selected account pre-checked
 */
function renderEditAccountTree(accounts, selectedAccountId, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        const isSelected = accountId === selectedAccountId;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleAccountTreeItem('edit-tree-${accountId}')" 
                                class="mr-1 w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600">
                            <svg id="edit-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="edit-account-selection" value="${accountId}" 
                               ${isSelected ? 'checked' : ''}
                               onchange="selectEditAccount('${accountId}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="edit-tree-${accountId}" class="hidden">
                        ${renderEditAccountTree(account.children, selectedAccountId, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Select account in edit modal
 */
export function selectEditAccount(accountId) {
    const hiddenInput = document.getElementById('edit-user-account');
    if (hiddenInput) {
        hiddenInput.value = accountId;
    }
}

/**
 * Expand tree nodes to show a specific account
 */
function expandTreeToAccount(accounts, targetAccountId, prefix = 'edit-tree-') {
    for (const account of accounts) {
        const accountId = account._id || account.id;
        
        // Check if this account or its children contain the target
        if (accountId === targetAccountId) {
            return true; // Found it
        }
        
        if (account.children && account.children.length > 0) {
            // Recursively check children
            const foundInChildren = expandTreeToAccount(account.children, targetAccountId, prefix);
            if (foundInChildren) {
                // Expand this node because target is in children
                const childContainer = document.getElementById(`${prefix}${accountId}`);
                const icon = document.getElementById(`${prefix}icon-${accountId}`);
                
                if (childContainer) {
                    childContainer.classList.remove('hidden');
                }
                if (icon) {
                    icon.classList.add('rotate-90');
                }
                return true;
            }
        }
    }
    return false;
}

/**
 * Update user data
 */
export async function updateUser() {
    try {
        // Clear any previous error states
        clearModalError('edit-user-error');
        const emailField = document.getElementById('edit-user-email');
        if (emailField) {
            emailField.classList.remove('border-red-500');
        }
        const nfcFieldReset = document.getElementById('edit-user-nfc-card-id');
        if (nfcFieldReset) {
            nfcFieldReset.classList.remove('border-red-500');
        }
        
        const userId = document.getElementById('edit-user-id').value;
        const form = document.getElementById('edit-user-form');
        const formData = new FormData(form);
        
        const userData = {
            role: formData.get('role'),
            account_id: formData.get('account_id'),
            full_name: formData.get('full_name'),
            employee_id: formData.get('employee_id') || '',
            email: formData.get('email'),
            phone_number: formData.get('phone_number') || '',
            department: formData.get('department') || '',
            nfc_card_ids: parseNfcCardInput(formData.get('nfc_card_id')),
            work_address: formData.get('work_address') || '',
            is_active: document.getElementById('edit-user-is-active')?.checked ?? false
        };

        // Collect time tracking account IDs (Team leader / Team viewer only)
        const ttSection = document.getElementById('edit-tt-accounts-section');
        if (ttSection && !ttSection.classList.contains('hidden')) {
            const ttCheckboxes = ttSection.querySelectorAll('input[type="checkbox"]:checked');
            userData.time_tracking_account_ids = Array.from(ttCheckboxes).map(cb => cb.value);
        } else {
            userData.time_tracking_account_ids = [];
        }
        
        const sendWelcomeCheckbox = document.getElementById('edit-send-welcome-email');
        if (sendWelcomeCheckbox) {
            userData.send_welcome_email = sendWelcomeCheckbox.checked;
        }
        
        await apiCall(`/users/${userId}`, {
            method: 'PUT',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify(userData)
        });
        
        // If editing current user, refresh their data in localStorage
        const currentUser = getCurrentUser();
        const editedUser = await apiCall(`/users/${userId}`, {
            headers: auth.getAuthHeaders()
        });
        
        if (currentUser && currentUser.username === editedUser.username) {
            // Update localStorage with fresh data from /auth/me endpoint
            const updatedUser = await getCurrentUserFromAPI();
            localStorage.setItem('current_user', JSON.stringify(updatedUser));
            console.log('✅ Current user data refreshed in localStorage');
        }
        
        closeModal();
        showSuccess(t('users.updateSuccess'));
        loadUsersData(true);  // Reset and reload to show updated user
    } catch (error) {
        // Check if it's an email duplicate error
        if (error.message && error.message.toLowerCase().includes('email already')) {
            const emailField = document.getElementById('edit-user-email');
            if (emailField) {
                emailField.classList.add('border-red-500');
            }
            showModalError('edit-user-error', t('users.emailAlreadyRegistered'));
        } else if (error.message && error.message.toLowerCase().includes('nfc card id')) {
            // NFC card already assigned to another user
            const nfcField = document.getElementById('edit-user-nfc-card-id');
            if (nfcField) {
                nfcField.classList.add('border-red-500');
                nfcField.focus();
            }
            showModalError('edit-user-error', error.message);
        } else {
            showModalError('edit-user-error', t('users.updateError') + ': ' + (error.message || 'Ukendt fejl'));
        }
        console.error('Error updating user:', error);
    }
}

/**
 * Delete user
 */
export async function deleteUser(userId, userName) {
    const confirmed = await showConfirmDialog({
        title: t('users.delete'),
        message: `${t('users.deleteConfirm')} "${userName}"? ${t('users.deleteWarning')}`,
        confirmText: t('users.delete'),
        cancelText: t('common.cancel'),
        confirmClass: 'bg-red-600 hover:bg-red-500',
        icon: 'danger'
    });
    
    if (!confirmed) {
        return;
    }

    try {
        await apiCall(`/users/${userId}`, {
            method: 'DELETE',
            headers: auth.getAuthHeaders()
        });

        closeModal();
        showSuccess(t('users.deleteSuccess'));
        loadUsersData(true);  // Reset and reload after deletion
    } catch (error) {
        showError(t('users.deleteError') + ': ' + (error.message || 'Ukendt fejl'));
        console.error('Error deleting user:', error);
    }
}

/**
 * Resend invitation to user
 */
export async function resendInvitation(userId, userName) {
    const confirmed = await showConfirmDialog({
        title: 'Resend Invitation',
        message: `Resend invitation email to "${userName}"?`,
        confirmText: 'Send Invitation',
        cancelText: 'Cancel',
        confirmClass: 'bg-primary-600 hover:bg-primary-500',
        icon: 'info'
    });
    
    if (!confirmed) {
        return;
    }

    try {
        const response = await apiCall(`/users/${userId}/resend-invitation`, {
            method: 'POST',
            headers: auth.getAuthHeaders()
        });
        
        // Check if there's a warning about token replacement
        if (response.warning && response.warning.type === 'token_replaced') {
            showSuccess('Invitation email sent successfully!');
            
            // Show additional warning about previous token being invalidated
            setTimeout(() => {
                showWarning(
                    `⚠️ Important: Previous invitation link has been invalidated. ` +
                    `If "${userName}" already received an invitation email, they must use the NEW link from the email just sent. ` +
                    `Old links will show "Unable to verify invitation token".`,
                    10000 // Show for 10 seconds
                );
            }, 500);
        } else {
            showSuccess('Invitation email sent successfully!');
        }
    } catch (error) {
        showError('Failed to resend invitation: ' + (error.message || 'Unknown error'));
        console.error('Error resending invitation:', error);
    }
}

/**
 * Close modal helper (needs to be global)
 */
function closeModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.classList.add('hidden');
        modal.innerHTML = '';
    }
}

// Make functions globally accessible for onclick handlers
if (typeof window !== 'undefined') {
    window.closeModal = closeModal;
    window.toggleWelcomeEmailOption = toggleWelcomeEmailOption;
    window.toggleEditWelcomeEmailOption = toggleEditWelcomeEmailOption;
    window.toggleAccountTreeItem = toggleAccountTreeItem;
    window.selectAccount = selectAccount;
    window.selectEditAccount = selectEditAccount;
    window.createUser = createUser;
    window.updateUser = updateUser;
    window.deleteUser = deleteUser;
    window.resendInvitation = resendInvitation;
    window.editUser = editUser;
    window.showCreateUserModal = showCreateUserModal;
}

export default {
    showCreateUserModal,
    toggleWelcomeEmailOption,
    createUser,
    editUser,
    updateUser,
    deleteUser,
    resendInvitation,
    toggleEditWelcomeEmailOption,
    toggleAccountTreeItem,
    selectAccount,
    selectEditAccount
};
